import { type_currency } from '~/utils/constants/home.js'

/**
 * Formats a numeric value as a localized currency string using `Intl.NumberFormat`.
 * @param {number} value - The amount to format.
 * @param {string} type_money - Currency slug (e.g. `'EUR'`, `'USD'`).
 * @returns {string|number} Formatted string, or `0` for invalid inputs.
 */
export const formatCurrency = (value, type_money) => {
	if (typeof value !== 'number' || !Number.isFinite(value)) return 0

	const find_currency = type_currency.find((item) => item.slug === type_money)

	const options = {
		style: 'currency',
		currency: type_money,
	}

	const locale = find_currency.currency || type_currency[0].currency

	return new Intl.NumberFormat(locale, options).format(value)
}

const formatMillions = (value) => {
	if (value < 1_000_000) {
		return null
	}

	return Math.round(value / 1_000_000)
}

/**
 * Formats a large currency value, abbreviating millions as `'X M'` or `'X Million'`.
 * Values under 1 000 000 are formatted with standard `Intl.NumberFormat`.
 * @param {number} value - The amount to format.
 * @param {string} slug - Currency slug (e.g. `'EUR'`).
 * @param {'short'|'long'} [style='short'] - Controls the suffix (`'M'` vs `'Million'`).
 * @returns {string}
 */
export const formatCurrencySize = (value, slug, style = 'short') => {
	if (typeof value !== 'number') {
		throw new Error('El valor debe ser un número')
	}

	const currency = type_currency.find((c) => c.slug === slug)
	if (!currency) return value.toString()

	const millions = formatMillions(value)

	// 👇 Si no llega a 1M, usa formato normal
	if (!millions) {
		return new Intl.NumberFormat(currency.currency, {
			style: 'currency',
			currency: slug,
			maximumFractionDigits: 0,
		}).format(value)
	}

	const suffix = style === 'short' ? 'M' : 'Million'
	return `${currency.symbol} ${millions} ${suffix}`
}

/**
 * Returns the symbol character for a currency slug (e.g. `'€'` for `'EUR'`).
 * Falls back to the slug itself if not found.
 * @param {string} slug
 * @returns {string}
 */
export const getCurrencySymbol = (slug) => {
	const found = type_currency.find((item) => item.slug === slug)
	return found?.symbol ?? slug
}
